import React from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM } from 'components/atoms';
import Text from 'components/atoms/Text';
import SlidePopup from 'components/organisms/CommonUI/SlidePopup';
import ToastPopup from 'components/organisms/CommonUI/ToastPopup';
import ConfirmPopup from 'components/organisms/CommonUI/ConfirmPopup';

const LayoutContainer = styled.div`
  min-height: 100vh;
  background: ${COLOR_SYSTEM.get('Gray')?.[50]};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.header`
  width: 100%;
  padding: 16px 24px;
  background: #ffffff;
  border-bottom: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
  display: flex;
  justify-content: center;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LogoImage = styled.img`
  height: 28px;
`;

const ServiceName = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get('Gray')?.[800]};
`;

const StepIndicatorContainer = styled.div`
  width: 100%;
  max-width: 480px;
  padding: 20px 16px 0;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const StepDot = styled.div<{ $active: boolean; $completed: boolean }>`
  width: ${(props) => props.$active ? '34px' : '20px'};
  height: ${(props) => props.$active ? '34px' : '20px'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => props.$active ? '15px' : '11px'};
  font-weight: 600;
  flex-shrink: 0;
  background: ${(props) =>
    props.$completed
      ? COLOR_SYSTEM.get('Blue')?.[100]
      : props.$active
        ? COLOR_SYSTEM.get('Blue')?.[500]
        : COLOR_SYSTEM.get('Gray')?.[200]};
  color: ${(props) =>
    props.$completed
      ? COLOR_SYSTEM.get('Blue')?.[600]
      : props.$active
        ? '#ffffff'
        : COLOR_SYSTEM.get('Gray')?.[500]};
  transition: all 0.3s ease;
`;

const StepLine = styled.div<{ $completed: boolean }>`
  height: 2px;
  width: 40px;
  margin-bottom: 20px;
  background: ${(props) =>
    props.$completed ? COLOR_SYSTEM.get('Blue')?.[300] : COLOR_SYSTEM.get('Gray')?.[200]};
`;

const STEP_LABELS = ['동의', '본인인증', '처방전 전달'];

const StepIndicator: React.FC<{ step: number }> = ({ step }) => (
  <StepIndicatorContainer>
    <StepRow>
      {STEP_LABELS.map((label, i) => (
        <React.Fragment key={i}>
          {i > 0 && <StepLine $completed={step > i} />}
          <StepItem>
            <StepDot $active={step === i + 1} $completed={step > i + 1}>
              {i + 1}
            </StepDot>
            <Text
              textType="Caption"
              color={step === i + 1
                ? COLOR_SYSTEM.get('Blue')?.[600]
                : step > i + 1
                  ? COLOR_SYSTEM.get('Blue')?.[500]
                  : COLOR_SYSTEM.get('Gray')?.[400]}
              style={{ fontWeight: step === i + 1 || step > i + 1 ? 600 : 400 }}
            >
              {step > i + 1 ? `${label} 완료` : label}
            </Text>
          </StepItem>
        </React.Fragment>
      ))}
    </StepRow>
  </StepIndicatorContainer>
);

const Content = styled.main`
  width: 100%;
  max-width: 480px;
  padding: 24px 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

interface NhisLayoutProps {
  children: React.ReactNode;
  step?: number;
}

const NhisLayout: React.FC<NhisLayoutProps> = ({ children, step }) => {
  return (
    <LayoutContainer>
      <Header>
        <LogoSection>
          <LogoImage src="/images/logo.png" alt="약문약답 로고" />
          <ServiceName>PhAI</ServiceName>
        </LogoSection>
      </Header>
      {step && step >= 1 && step <= 3 && <StepIndicator step={step} />}
      <Content>{children}</Content>
      <SlidePopup />
      <ToastPopup />
      <ConfirmPopup />
    </LayoutContainer>
  );
};

export { StepIndicator };
export default NhisLayout;
